import React, { useEffect, useState, useRef } from "react";
import { Route, Switch, useParams } from "react-router-dom";
import { RemoteModuleLoader, SuspenseAndErrorFallback } from "./remote-modules-loader";
import { onAuthStateChanged, removeOnAuthStateChanged } from "./lib/firebase-auth";
import { getCurrentUser } from "@glide/common-core/dist/js/utility/material-app-facilities";
import { logError } from "@glide/support";
import { GlideIcon } from "@glide/common";

interface ModuleLabParams {
    orgID?: string;
    appID?: string;
}

type BuilderContext = {
    orgID?: string;
    appID?: string;
    firebaseUserToken?: string;
};

// Future! We could export this from modules
type ExampleModuleProps = {
    name: string;
    builderContext: BuilderContext;
};

const ExampleComponent = RemoteModuleLoader.get().loadRemoteComponent<ExampleModuleProps>(
    "example",
    "ExampleComponent"
);

const ExampleComponentDemo = RemoteModuleLoader.get().loadRemoteComponent<any>("demo", "ExampleComponentDemo");

type ModuleOption = {
    label: string;
    value: string;
};

const availableModules: ModuleOption[] = [
    { label: "Select a module...", value: "" },
    { label: "ExampleComponent", value: "example" },
    { label: "ExampleComponentDemo", value: "example-from-test" },
];

const ModuleLabContent: React.FC = () => {
    const { orgID, appID } = useParams<ModuleLabParams>();
    const [builderContext, setBuilderContext] = useState<BuilderContext>({
        orgID,
        appID,
    });
    const [selectedModule, setSelectedModule] = useState<string>("");
    const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const hasTokenRef = useRef<boolean>(false);

    useEffect(() => {
        const updateBuilderContext = async () => {
            try {
                const user = await getCurrentUser();
                const firebaseUserToken = user ? await user.getIdToken() : undefined;

                if (firebaseUserToken && !hasTokenRef.current) {
                    hasTokenRef.current = true;
                    setBuilderContext(prev => ({
                        ...prev,
                        firebaseUserToken,
                    }));

                    // Stop polling once we have the token
                    if (pollIntervalRef.current) {
                        clearInterval(pollIntervalRef.current);
                        pollIntervalRef.current = null;
                    }
                }
            } catch (error: unknown) {
                logError("Error fetching user token:", error);
            }
        };

        const authStateChangedCallback = () => {
            void updateBuilderContext();
        };

        onAuthStateChanged(authStateChangedCallback);

        // Polling fallback - check auth state every 1 second
        pollIntervalRef.current = setInterval(() => {
            void updateBuilderContext();
        }, 1000);

        // Try to get token immediately
        void updateBuilderContext();

        return () => {
            removeOnAuthStateChanged(authStateChangedCallback);
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
            }
        };
    }, []);

    // Update orgID and appID when route params change
    useEffect(() => {
        setBuilderContext(prev => ({
            ...prev,
            orgID,
            appID,
        }));
    }, [orgID, appID]);

    return (
        <div tw="h-screen flex flex-col">
            {/* Top Bar */}
            <div tw="bg-bg-middle border-b border-border-base p-4 flex items-center justify-between">
                <div tw="flex items-center space-x-2">
                    <GlideIcon kind="stroke" icon="st-research-lab" iconSize={24} strokeColor="var(--gv-text-base)" />
                    <h1 tw="text-xl font-semibold text-text-dark">Module Lab</h1>
                </div>
                <div tw="flex items-center space-x-4">
                    <div tw="relative">
                        <select
                            tw="px-3 py-2 pr-8 border border-border-base rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent text-text-base appearance-none bg-bg-front w-full"
                            value={selectedModule}
                            onChange={e => setSelectedModule(e.target.value)}>
                            {availableModules.map(module => (
                                <option key={module.value} value={module.value}>
                                    {module.label}
                                </option>
                            ))}
                        </select>
                        <div tw="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                            <svg tw="w-5 h-5 text-text-pale" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                    fillRule="evenodd"
                                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div tw="flex-1 p-4 overflow-auto bg-bg-back">
                {selectedModule === "example" && (
                    <SuspenseAndErrorFallback
                        loading={<div>Loading...</div>}
                        error={<div>Error loading ExampleComponent from remote</div>}>
                        <ExampleComponent builderContext={builderContext} name="Module Lab User" />
                    </SuspenseAndErrorFallback>
                )}
                {selectedModule === "example-from-test" && (
                    <SuspenseAndErrorFallback
                        loading={<div>Loading...</div>}
                        error={<div>Error loading ExampleComponentDemo from remote</div>}>
                        <ExampleComponentDemo />
                    </SuspenseAndErrorFallback>
                )}
                {!selectedModule && <div tw="text-center text-text-pale mt-8">Please select a module to view</div>}
            </div>
        </div>
    );
};

export const ModuleDev: React.FC = () => {
    return (
        <Switch>
            <Route exact path="/module-lab" component={ModuleLabContent} />
            <Route exact path="/module-lab/:orgID" component={ModuleLabContent} />
            <Route exact path="/module-lab/:orgID/:appID" component={ModuleLabContent} />
        </Switch>
    );
};
